import { useState } from 'react'
import { Trans } from 'react-i18next'
import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { premiumFontCss } from '@root/entities/premium/ui'
import { Drawer } from '@root/shared/ui-kit'

import { BuyPremium } from '../../common/buy-premium'
import { BuyPremiumSafetyCheck } from '../../common/buy-premium-safety-check'

import type { CurrencyModel } from '@root/entities/currency'
import type { PremiumMetaModel } from '@root/entities/premium'
import type { DrawerProps } from '@root/shared/ui-kit'

type Step = 'safety-check' | 'buy-premium'

type Props = Pick<DrawerProps, 'open' | 'onOpenChange'> & {
  currency: CurrencyModel
  premium: PremiumMetaModel | undefined
  shouldCheckSafety?: boolean
}

export const BuyPremiumDrawer: React.FC<Props> = ({
  open,
  onOpenChange,
  currency,
  premium,
  shouldCheckSafety = true,
}) => {
  const [step, setStep] = useState<Step>(
    shouldCheckSafety ? 'safety-check' : 'buy-premium',
  )

  const renderContent = () => {
    if (step === 'safety-check') {
      return (
        <BuyPremiumSafetyCheck
          currency={currency}
          onSuccess={() => setStep('buy-premium')}
        />
      )
    }
    return (
      <BuyPremium
        currency={currency}
        premium={premium}
        onClose={() => {
          onOpenChange?.(false)
        }}
        device="mobile"
      />
    )
  }

  return (
    <Root noBodyStyles open={open} onOpenChange={onOpenChange}>
      <Body>
        <Title>
          <Trans
            i18nKey={'premium.pay.subscription.title'}
            components={[<TitleAccent key={0} />]}
          />
        </Title>
        {renderContent()}
      </Body>
    </Root>
  )
}

const Root = styled(Drawer)``

const Body = styled.div`
  overflow: hidden;
  display: grid;

  padding: 10px 12px 32px;
`

const Title = styled(Typography).attrs({
  variant: 'body1-bold',
  component: 'h2',
})`
  color: var(--backgroundA-11);
  text-align: center;
  margin-bottom: 32px;
`

const TitleAccent = styled(Typography).attrs({
  variant: 'h4',
  component: 'span',
})`
  ${premiumFontCss}
  font-size: 18px;
  line-height: 20px;
`
